import { MutatorResultErrorDetails, QueryResultType, TypedView, Zero } from '@rocicorp/zero'
import { captureException } from '@sentry/react'
import {
	AcceptInviteResponseBody,
	CreateFilesResponseBody,
	FILE_PREFIX,
	LOCAL_FILE_PREFIX,
	MAX_NUMBER_OF_FILES,
	ROOM_PREFIX,
	Snapshot,
	TlaFile,
	WELCOME_CREATE_SOURCE,
	TlaFileState,
	TlaFileStatePartial,
	TlaFlags,
	TlaGroupFile,
	TlaMutators,
	TlaSchema,
	TlaUser,
	UserPreferencesKeys,
	ZErrorCode,
	ZeroContext,
	can,
	createMutators,
	parseFlags,
	queries,
	schema as zeroSchema,
} from '@tldraw/dotcom-shared'
import {
	Result,
	compact,
	fetch,
	isEqual,
	promiseWithResolve,
	sleep,
	sortByIndex,
	sortByMaybeIndex,
	structuredClone,
	throttle,
	uniqueId,
} from '@tldraw/utils'
import { useNavigate } from 'react-router-dom'
import {
	Atom,
	Signal,
	TLDocument,
	TLUiToastsContextType,
	TLUserPreferences,
	assertExists,
	atom,
	computed,
	createTLCurrentUser,
	createTLSchema,
	dataUrlToFile,
	defaultUserPreferences,
	getUserPreferences,
	parseTldrawJsonFile,
	react,
	transact,
} from 'tldraw'
import { routes } from '../../routeDefs'
import { trackEvent } from '../../utils/analytics'
import { ZERO_SERVER } from '../../utils/config'
import { getFirstLoadId, markFirstLoad } from '../../utils/firstLoad'
import { multiplayerAssetStore } from '../../utils/multiplayerAssetStore'
import { getScratchPersistenceKey } from '../../utils/scratch-persistence-key'
import { mergeCommentFeeds } from '../components/TlaSidebar/components/commentNotifications'
import { TLAppUiContextType, TLAppUiEventSource } from '../utils/app-ui-events'
import { copyTextToClipboard } from '../utils/copy'
import { getDateFormat } from '../utils/dates'
import { FeatureFlags } from '../utils/featureFlags'
import { createIntl, defineMessages, setupCreateIntl } from '../utils/i18n'
import {
	clearLastVisitedFile,
	getLastVisitedFileId,
	updateLocalSessionState,
} from '../utils/local-session-state'
import { ZeroLogBuffer, formatLogArg, redactTokens } from './ZeroLogBuffer'

export const TLDR_FILE_ENDPOINT = `/api/app/tldr`
export const PUBLISH_ENDPOINT = `/api/app/publish`

const USER_PRELOAD_TIMEOUT_MS = 30_000

export interface PreloadDiagnostics {
	stage: string
	initError: string | undefined
	connection: string
	connectionReason: string | undefined
	visibilityState: DocumentVisibilityState
	hiddenMs: number
	online: boolean
	msSinceNavigation: number
	zeroLog: string[]
}

export function getPreloadDiagnostics(error: unknown): PreloadDiagnostics | undefined {
	return (error as { diagnostics?: PreloadDiagnostics } | null)?.diagnostics
}

let appId = 0

/** When the user last opened the file (visit, else edit, else first visit), or undefined if never. */
export function getFileVisitDate(state: TlaFileState | undefined): number | undefined {
	return state?.lastVisitAt ?? state?.lastEditAt ?? state?.firstVisitAt ?? undefined
}

/** Ranks a file for display: when it was last opened, else when it was created. */
export function getFileRecencyDate(
	state: TlaFileState | undefined,
	file: TlaFile | undefined
): number {
	return getFileVisitDate(state) ?? file?.createdAt ?? 0
}

/** Milliseconds until a JWT's `exp`, or null if it can't be read. */
function msUntilExpiry(token: string | undefined, now = Date.now()): number | null {
	if (!token) return null
	try {
		const [, payload] = token.split('.')
		if (!payload) return null
		// base64url, which atob doesn't accept
		const claims = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')))
		if (typeof claims?.exp !== 'number') return null
		return claims.exp * 1000 - now
	} catch {
		// a token we can't parse is still a usable token; fall back to the fixed cadence
		return null
	}
}

/** Fallback cadence for a token whose expiry we can't read. Under Clerk's 60s default this is
 *  already too slow for a hidden tab — see {@link msUntilTokenRefresh}. */
const FALLBACK_TOKEN_REFRESH_MS = 50_000
/** Never busy-loop on a token that is expired or nearly so. */
const MIN_TOKEN_REFRESH_MS = 5_000

/**
 * When to refresh the auth token, derived from the token's own expiry rather than a fixed cadence.
 *
 * This matters more than a normal token refresh because Zero hands the token to zero-cache, which
 * reuses it for the transform and push fetches behind the connection. An expired token there
 * doesn't fail one request, it invalidates the whole connection — which is what produced a steady
 * ~2k `TransformFailed` and ~600 connection invalidations per hour in production.
 *
 * Refreshing at half the remaining life means a tick that runs late still has a whole half-life of
 * slack. Reading the deadline off the token also means raising the session-token lifetime in the
 * Clerk dashboard takes effect here with no code change — which is the part that actually fixes
 * this. Browsers throttle timers in hidden tabs to roughly once a minute, so against Clerk's 60s
 * default the token expires under a backgrounded tab no matter what cadence we ask for; no
 * client-side schedule can beat that, only a longer-lived token can.
 */
export function msUntilTokenRefresh(token: string | undefined, now = Date.now()): number {
	const remaining = msUntilExpiry(token, now)
	if (remaining === null) return FALLBACK_TOKEN_REFRESH_MS
	return Math.max(MIN_TOKEN_REFRESH_MS, remaining / 2)
}

export class TldrawApp {
	config = {
		maxNumberOfFiles: MAX_NUMBER_OF_FILES,
	}

	readonly id = appId++

	readonly z: Zero<TlaSchema, TlaMutators, ZeroContext>

	private readonly user$: Signal<TlaUser | undefined>
	// These signal types are derived from the query definitions in dotcom-shared rather than
	// hand-written, so the shape (and the nullability of `.one()` joins like `file`) stays in
	// sync with the queries automatically. A previous hand-written annotation claimed `file` was
	// always present, which hid a production crash when a `.one()` join resolved to undefined.
	private readonly fileStates$: Signal<QueryResultType<typeof queries.fileStates>>
	private readonly workspaceMemberships$: Signal<
		QueryResultType<typeof queries.workspaceMemberships>
	>
	/**
	 * The comment feeds (one per reason for a notification — see `homeBoardComments` in
	 * dotcom-shared), each empty until {@link startNotificationFeeds} subscribes it.
	 */
	private readonly homeBoardComments$: Atom<QueryResultType<typeof queries.homeBoardComments>>
	private readonly threadStarterComments$: Atom<
		QueryResultType<typeof queries.threadStarterComments>
	>
	private readonly threadParticipantComments$: Atom<
		QueryResultType<typeof queries.threadParticipantComments>
	>
	private readonly mentionComments$: Atom<QueryResultType<typeof queries.mentionComments>>
	/** The feeds merged, deduped by comment id (a comment can qualify for several reasons). */
	private readonly comments$: Signal<QueryResultType<typeof queries.homeBoardComments>>
	/** Like the comment feeds. */
	private readonly reactions$: Atom<QueryResultType<typeof queries.reactions>>
	/** The signed-in account's email, for the load report gate. */
	readonly email: string | null
	readonly isLoadRumEnabled: boolean

	private readonly abortController = new AbortController()
	readonly disposables: (() => void)[] = [() => this.abortController.abort(), () => this.z.close()]
	private getToken: () => Promise<string | undefined>

	changes: Map<Atom<any, unknown>, any> = new Map()
	changesFlushed = null as null | ReturnType<typeof promiseWithResolve>
	private readonly zeroLog = new ZeroLogBuffer()

	// Track new room creation timestamps and sources
	private newRoomCreationStartTimes: Map<string, { startTime: number; source: string }> = new Map()

	private signalizeQuery<TReturn>(name: string, query: any): Signal<TReturn> {
		// fail if closed?
		const view = this.z.materialize(query) as unknown as TypedView<TReturn>
		const val$ = atom(name, view.data, { isEqual })
		this.bindQuery(val$, view)
		return val$
	}

	/** Feed a materialized Zero view into an atom for its lifetime, batched like every other signal. */
	private bindQuery<TReturn>(val$: Atom<TReturn>, view: TypedView<TReturn>) {
		let reportedError = false
		view.addListener((res, resultType, error) => {
			// a failed query just leaves its signal empty, which looks like no data rather than broken.
			// Closing Zero fails every query still hydrating, which is teardown, not a failure
			if (resultType === 'error' && !reportedError && !this.z.closed) {
				reportedError = true
				captureException(new Error(`Query failed: ${val$.name}`), { extra: { error } })
			}
			this.changes.set(val$, structuredClone(res))
			if (!this.changesFlushed) {
				this.changesFlushed = promiseWithResolve()
			}
			queueMicrotask(() => {
				transact(() => {
					this.changes.forEach((value, key) => {
						key.set(value)
					})
					this.changes.clear()
				})
				this.changesFlushed?.resolve(undefined)
				this.changesFlushed = null
			})
		})
		this.disposables.push(() => {
			view.destroy()
		})
	}

	toasts: TLUiToastsContextType | null = null
	trackEvent: TLAppUiContextType
	navigate: ReturnType<typeof useNavigate>

	/**
	 * Test-only hook (only set under Playwright, see the constructor) that fires the same
	 * "client too old" callback Zero's `onUpdateNeeded` would call, so e2e can exercise the
	 * reload-recovery UI without forcing a real schema/protocol mismatch against zero-cache.
	 */
	__test__triggerClientTooOld?: () => void

	private constructor(
		public readonly userId: string,
		initialZeroToken: string | undefined,
		getToken: () => Promise<string | undefined>,
		getZeroToken: () => Promise<string | undefined>,
		onClientTooOld: () => void,
		trackEvent: TLAppUiContextType,
		navigate: ReturnType<typeof useNavigate>,
		flags: FeatureFlags,
		email?: string | null
	) {
		this.navigate = navigate
		this.trackEvent = trackEvent
		this.getToken = getToken
		this.email = email ?? null
		this.isLoadRumEnabled = flags.load_rum?.enabled ?? false
		// Exposed as __test__triggerClientTooOld below so e2e can exercise the real recovery UI
		// without a live schema/protocol mismatch against zero-cache.
		if (window.navigator.webdriver) {
			this.__test__triggerClientTooOld = () => onClientTooOld()
		}
		const z = new Zero<TlaSchema, TlaMutators, ZeroContext>({
			auth: initialZeroToken,
			userID: userId,
			schema: zeroSchema,
			cacheURL: ZERO_SERVER,
			mutators: createMutators(userId),
			context: { userId } satisfies ZeroContext,
			logLevel: 'info',
			logSink: this.zeroLog,
			onUpdateNeeded(reason) {
				console.error('update needed', reason)
				onClientTooOld()
			},
			kvStore: window.navigator.webdriver ? 'mem' : 'idb',
		})
		this.z = z
		// Refresh the token ahead of its own expiry and reschedule from whatever we get back, so the
		// cadence follows the token's lifetime rather than a hardcoded guess at it. In Zero 0.26+
		// this sends an updateAuth message without reconnecting.
		let refreshTimeout: ReturnType<typeof setTimeout> | undefined
		const scheduleRefresh = (token: string | undefined) => {
			clearTimeout(refreshTimeout)
			refreshTimeout = setTimeout(() => {
				refreshToken().catch((err) => {
					console.error('Failed to proactively refresh auth token:', err)
				})
			}, msUntilTokenRefresh(token))
		}
		// Reschedule on every outcome — a rejected getZeroToken() or a throwing connect() must not
		// kill the refresh chain, so scheduling happens before connect and in the reject path.
		const refreshToken = () =>
			getZeroToken()
				.catch((err) => {
					scheduleRefresh(undefined)
					throw err
				})
				.then((token) => {
					scheduleRefresh(token)
					if (token) {
						z.connection.connect({ auth: token })
					}
					return !!token
				})
		scheduleRefresh(initialZeroToken)
		this.disposables.push(() => clearTimeout(refreshTimeout))
		// Timers in a hidden tab are throttled to about once a minute, so the refresh has to sit well
		// inside that budget — see the `zero` JWT template's lifetime. Refreshing on the way back
		// covers the cases no schedule can (a frozen tab, a slept laptop), where the token goes stale
		// while nothing is running at all.
		const onVisibilityChange = () => {
			if (document.visibilityState !== 'visible') return
			refreshToken().catch((err) => {
				console.error('Failed to refresh auth token on focus:', err)
			})
		}
		document.addEventListener('visibilitychange', onVisibilityChange)
		this.disposables.push(() =>
			document.removeEventListener('visibilitychange', onVisibilityChange)
		)
		// Set up token refresh on auth errors with backoff
		let authRetryCount = 0
		const MAX_AUTH_RETRIES = 5
		const unsubscribe = z.connection.state.subscribe((state) => {
			if (state.name === 'needs-auth') {
				if (authRetryCount >= MAX_AUTH_RETRIES) {
					console.error(`Auth retry limit reached (${MAX_AUTH_RETRIES}), giving up`)
					captureException(new Error('Auth retry limit reached'))
					return
				}
				const delay = Math.min(1000 * Math.pow(2, authRetryCount), 30_000)
				authRetryCount++
				setTimeout(() => {
					refreshToken()
						.then((didRefresh) => {
							if (didRefresh) authRetryCount = 0
						})
						.catch((err) => {
							console.error('Failed to refresh auth token:', err)
							captureException(err)
						})
				}, delay)
			}
		})
		this.disposables.push(unsubscribe)

		this.user$ = this.signalizeQuery('user signal', queries.user())
		this.fileStates$ = this.signalizeQuery('file states signal', queries.fileStates())
		this.workspaceMemberships$ = this.signalizeQuery(
			'workspace memberships signal',
			queries.workspaceMemberships()
		)
		this.homeBoardComments$ = atom('home board comments signal', [], { isEqual })
		this.threadStarterComments$ = atom('thread starter comments signal', [], { isEqual })
		this.threadParticipantComments$ = atom('thread participant comments signal', [], { isEqual })
		this.mentionComments$ = atom('mention comments signal', [], { isEqual })
		this.comments$ = computed('comments signal', () =>
			mergeCommentFeeds<QueryResultType<typeof queries.homeBoardComments>[number]>(
				this.homeBoardComments$.get(),
				this.threadStarterComments$.get(),
				this.threadParticipantComments$.get(),
				this.mentionComments$.get()
			)
		)
		this.reactions$ = atom('reactions signal', [], { isEqual })
	}

	/**
	 * Subscribe the notifications feeds. Called once {@link preload} has resolved rather than from
	 * the constructor: nothing awaits these, but a query the view-syncer is hydrating still
	 * delays the bootstrap queries the app does wait on (tldraw-internal#2032).
	 */
	startNotificationFeeds() {
		this.bindQuery(
			this.homeBoardComments$,
			this.materializeQuery<QueryResultType<typeof queries.homeBoardComments>>(
				queries.homeBoardComments()
			)
		)
		this.bindQuery(
			this.threadStarterComments$,
			this.materializeQuery<QueryResultType<typeof queries.threadStarterComments>>(
				queries.threadStarterComments()
			)
		)
		this.bindQuery(
			this.threadParticipantComments$,
			this.materializeQuery<QueryResultType<typeof queries.threadParticipantComments>>(
				queries.threadParticipantComments()
			)
		)
		this.bindQuery(
			this.mentionComments$,
			this.materializeQuery<QueryResultType<typeof queries.mentionComments>>(
				queries.mentionComments()
			)
		)
		this.bindQuery(
			this.reactions$,
			this.materializeQuery<QueryResultType<typeof queries.reactions>>(queries.reactions())
		)
	}

	/**
	 * Recent comments across the user's files, for the notifications feed (bounded per feed,
	 * cross-file, unordered). Empty until {@link startNotificationFeeds}.
	 */
	getComments(): QueryResultType<typeof queries.homeBoardComments> {
		return this.comments$.get()
	}

	/**
	 * Recent reactions to the user's comments across their files, for the notifications feed
	 * (bounded, cross-file). Empty until {@link startNotificationFeeds}.
	 */
	getReactions(): QueryResultType<typeof queries.reactions> {
		return this.reactions$.get()
	}

	/**
	 * Materialize an ad-hoc Zero query into a live view the caller owns and must `destroy()`. For
	 * parameterized, component-scoped queries (e.g. one file's comments) that shouldn't be
	 * app-lifetime signals like {@link fileStates$}.
	 */
	materializeQuery<TReturn>(query: unknown) {
		return this.z.materialize(query as any) as unknown as TypedView<TReturn>
	}

	/**
	 * Creates the user row + home workspace. Throws if the request was never sent; once sent, a
	 * failure is returned instead, since the row may still have committed.
	 */
	private async initUser(): Promise<Error | undefined> {
		const token = await this.getToken()
		if (!token) throw new Error('No auth token available for init')
		try {
			const res = await fetch(`/api/app/${this.userId}/init`, {
				method: 'POST',
				headers: { Authorization: `Bearer ${token}`, 'x-tldraw-load-id': getFirstLoadId() },
			})
			return res.ok ? undefined : new Error(`Init failed: ${res.status}`)
		} catch (e) {
			// Browsers word network errors differently and Sentry groups on the message.
			return new Error('Init request failed', { cause: e })
		}
	}

	async preload(signal?: AbortSignal) {
		// Zero's query can itself stall, so the deadline must cover it and every stage after it.
		// The stage is in the error so Sentry can tell a slow Zero sync from a row that never arrived.
		let stage: 'zero query' | 'state flush' | 'user init' | 'user record' | 'workspace data' =
			'zero query'
		const failed = promiseWithResolve<never>()
		let stopWaiting: (() => void) | undefined
		let initError: Error | undefined
		let hiddenMs = 0
		const fail = () => {
			const error =
				(stage === 'user record' && initError) || new Error(`Timed out waiting for the ${stage}`)
			try {
				const connection = this.z.connection.state.current
				// Sentry's ExtraErrorData integration copies this onto the event.
				Object.assign(error, {
					diagnostics: {
						stage,
						initError: initError && formatLogArg(initError),
						connection: connection.name,
						connectionReason:
							'reason' in connection ? redactTokens(formatLogArg(connection.reason)) : undefined,
						visibilityState: document.visibilityState,
						hiddenMs,
						online: navigator.onLine,
						msSinceNavigation: Math.round(performance.now()),
						zeroLog: this.zeroLog.recent(),
					} satisfies PreloadDiagnostics,
				})
			} finally {
				failed.reject(error)
			}
		}
		// Zero built in a hidden tab waits for visibility before connecting, so a restored or
		// cmd-clicked tab would burn the deadline before it gets a chance. Only count visible time,
		// and start over on return: Zero drops the socket after five minutes hidden, so whatever was
		// left of the budget would cover a cold reconnect only by luck.
		let timeout: ReturnType<typeof setTimeout> | undefined
		let hiddenSince = 0
		const resumeDeadline = () => {
			if (timeout !== undefined) return
			if (hiddenSince) {
				hiddenMs += Date.now() - hiddenSince
				hiddenSince = 0
			}
			timeout = setTimeout(fail, USER_PRELOAD_TIMEOUT_MS)
		}
		const pauseDeadline = () => {
			if (timeout === undefined) return
			clearTimeout(timeout)
			timeout = undefined
			hiddenSince = Date.now()
		}
		const onVisibilityChange = () =>
			document.visibilityState === 'visible' ? resumeDeadline() : pauseDeadline()
		document.addEventListener('visibilitychange', onVisibilityChange)
		if (document.visibilityState === 'visible') resumeDeadline()
		else hiddenSince = Date.now()
		// A hidden tab can sit here indefinitely, so the caller needs a way to settle this and let
		// create() dispose the half-built app when it gives up on it.
		const onAbort = () => failed.reject(new DOMException('Bootstrap cancelled', 'AbortError'))
		signal?.addEventListener('abort', onAbort)
		if (signal?.aborted) onAbort()
		try {
			await Promise.race([this.z.preload(queries.user()).complete, failed])
			stage = 'state flush'
			await Promise.race([this.changesFlushed, failed])
			const userLoaded = promiseWithResolve<void>()
			stopWaiting = react('wait for user', () => {
				if (this.user$.get()) userLoaded.resolve()
			})
			// Only a user Zero has confirmed missing needs init, so returning users never wait on the
			// worker's Postgres. The row wins over a slow init (another tab created it, a lost ack), and
			// a sent init that failed may still have committed, so its error only surfaces if the
			// deadline runs out.
			if (!this.user$.get()) {
				stage = 'user init'
				await Promise.race([
					this.initUser().then((error) => {
						initError = error
						// After the row wins, a late mark would skew the step deltas around it.
						if (stage === 'user init') markFirstLoad('init-done')
					}),
					userLoaded,
					failed,
				])
			}
			stage = 'user record'
			await Promise.race([userLoaded, failed])
			if (initError) console.warn('[AppState] User row arrived after init failed:', initError)
			markFirstLoad('zero-user-synced')
			stage = 'workspace data'
			await Promise.race([
				Promise.all([
					this.z.preload(queries.fileStates()).complete,
					this.z.preload(queries.workspaceMemberships()).complete,
				]),
				failed,
			])
			markFirstLoad('zero-preloaded')
		} finally {
			signal?.removeEventListener('abort', onAbort)
			document.removeEventListener('visibilitychange', onVisibilityChange)
			clearTimeout(timeout)
			stopWaiting?.()
		}
	}

	messages = defineMessages({
		max_workspaces_reached: {
			defaultMessage:
				'You have reached the maximum number of workspaces. You need to delete old workspaces before creating new ones.',
		},
		// the name of a workspace's seeded first (welcome) file
		new_workspace_file_name: { defaultMessage: 'Welcome to your workspace' },
		// toast title
		mutation_error_toast_title: { defaultMessage: 'Error' },
		// toast descriptions
		publish_failed: {
			defaultMessage: 'Unable to publish the file.',
		},
		unpublish_failed: {
			defaultMessage: 'Unable to unpublish the file.',
		},
		republish_failed: {
			defaultMessage: 'Unable to publish the changes.',
		},
		unknown_error: {
			defaultMessage: 'An unexpected error occurred.',
		},
		offline_error: {
			defaultMessage: 'You appear to be offline. Check your connection and try again.',
		},
		forbidden: {
			defaultMessage: 'You do not have the necessary permissions to perform this action.',
		},
		bad_request: {
			defaultMessage: 'Invalid request.',
		},
		rate_limit_exceeded: {
			defaultMessage: 'Rate limit exceeded, try again later.',
		},
		client_too_old: {
			defaultMessage: 'Please refresh the page to get the latest version of tldraw.',
		},
		max_files_title: {
			defaultMessage: 'File limit reached',
		},
		max_files_reached: {
			defaultMessage:
				'You have reached the maximum number of files. You need to delete old files before creating new ones.',
		},
		uploadingTldrFiles: {
			defaultMessage:
				'{total, plural, one {Uploading .tldr file…} other {Uploading {uploaded} of {total} .tldr files…}}',
		},
		addingTldrFiles: {
			// no need for pluralization, if there was only one file we navigated to it
			// so there's no need to show a toast.
			defaultMessage: 'Added {total} .tldr files.',
		},
	})

	getMessage(id: keyof typeof this.messages) {
		let msg = this.messages[id]
		if (!msg) {
			console.error('Could not find a translation for this error code', id)
			msg = this.messages.unknown_error
		}
		return msg
	}

	// Zero-level errors (socket down, client closed) carry prose, not a ZErrorCode.
	showMutationRejectionToast = throttle((error: MutatorResultErrorDetails['error']) => {
		const code = error.type === 'zero' ? ZErrorCode.offline_error : (error.message as ZErrorCode)
		const descriptor = this.getMessage(code)
		this.toasts?.addToast({
			title: this.getIntl().formatMessage(this.messages.mutation_error_toast_title),
			description: this.getIntl().formatMessage(descriptor),
		})
	}, 3000)

	dispose() {
		this.disposables.forEach((d) => d())
		// this.store.dispose()
	}

	/**
	 * Drops this user's Zero replica from IndexedDB. Zero keeps synced data on disk across sign-out
	 * so the next sign-in is fast, which leaks the previous user's files on shared machines. Safe to
	 * call after dispose(): delete() closes the instance first, and close() is idempotent.
	 */
	async deleteLocalData() {
		try {
			const { errors } = await this.z.delete()
			for (const error of errors) {
				captureException(error)
			}
		} catch (error) {
			captureException(error)
		}
	}

	getUser() {
		return assertExists(this.user$.get(), 'no user')
	}

	// Keep these helpers even when no per-user flags are active; future rollouts need
	// reactive access to the flags stored on the user record.
	@computed({ isEqual })
	getUserFlags(): Set<TlaFlags> {
		const user = this.getUser()
		return new Set(parseFlags(user.flags)) as Set<TlaFlags>
	}

	hasFlag(flag: TlaFlags) {
		return this.getUserFlags().has(flag)
	}

	/**
	 * Get the user's home workspace ID.
	 * Used to store shared files and pinned files.
	 * The home workspace ID is the same as the user ID.
	 */
	getHomeWorkspaceId() {
		return this.userId
	}

	/**
	 * A membership whose group row is missing is stale, not real. The comment feeds' access gate can
	 * keep the caller's group_user row in the client store after they leave a workspace (Zero 1.9's
	 * union fan-in drops the remove, rocicorp/mono#6636), while the group row, synced only by this
	 * query, is gone. Counting it would block rejoining by invite and pass client-side role checks.
	 */
	@computed({ isEqual })
	getWorkspaceMemberships() {
		return this.workspaceMemberships$
			.get()
			.filter((g) => g.group)
			.sort(sortByIndex)
	}

	getWorkspaceMembership(workspaceId: string) {
		return this.getWorkspaceMemberships().find((g) => g.groupId === workspaceId)
	}

	getWorkspaceFilesSorted(workspaceId: string) {
		const membership = this.getWorkspaceMembership(workspaceId)
		if (!membership) return []

		// Decide which of the membership's group_file rows actually belong in this list.
		// A file owned by this workspace always belongs. A non-home workspace lists only the
		// files it owns. The home workspace additionally lists legacy files (no owning group)
		// and "guest files" — shared files the user opened that are owned by a workspace they
		// are NOT a member of. It must NOT list a file owned by a workspace the user IS a
		// member of: that's a mislinked row (a guest file whose workspace was later joined, or
		// a workspace's welcome file mirrored during the create-workspace race), and opening it
		// from home would bounce the user into that workspace. Guarding here keeps such rows out
		// of the list even before they're cleaned up.
		const homeWorkspaceId = this.getHomeWorkspaceId()
		const groupFiles = membership.groupFiles.filter((f): f is TlaGroupFile & { file: TlaFile } => {
			// A group_file row can outlive (or arrive before) its file: the file may be deleted,
			// not yet synced, or filtered out server-side because the user can no longer read it.
			// The `file` relationship is a nullable Zero `.one()`, so guard before dereferencing.
			// The type predicate narrows `file` to non-undefined for the rest of the function.
			if (!f.file) return false
			const owningGroupId = f.file.owningGroupId
			if (owningGroupId === workspaceId) return true
			if (workspaceId !== homeWorkspaceId) return false
			return owningGroupId == null || !this.getWorkspaceMembership(owningGroupId)
		})

		const pinned = groupFiles.filter((f) => f.index !== null)
		const unpinned = groupFiles.filter((f) => f.index === null)

		const lastOrdering = this.lastWorkspaceFileOrderings.get(workspaceId)
		const retainedOrdering =
			lastOrdering?.filter((f) => unpinned.some((p) => p.fileId === f.fileId)) ?? []
		const newOrdering: typeof retainedOrdering = []

		pinned.sort(sortByMaybeIndex)

		for (const file of unpinned) {
			if (retainedOrdering.some((f) => f.fileId === file.fileId)) continue

			// For new files, use current updatedAt
			const state = this.getFileState(file.fileId)
			newOrdering.push({
				fileId: file.fileId,
				date: getFileRecencyDate(state, file.file),
			})
		}

		// Sort by date (most recent first) but only for new ordering
		newOrdering.sort((a, b) => b.date - a.date)

		const nextOrdering = [...newOrdering, ...retainedOrdering]
		// Store the ordering for next time
		this.lastWorkspaceFileOrderings.set(workspaceId, nextOrdering)

		// Return the actual file objects in the stable order
		return pinned
			.map((f) => ({ fileId: f.fileId, isPinned: f.index !== null, date: f.file.updatedAt }))
			.concat(nextOrdering.map((f) => ({ fileId: f.fileId, isPinned: false, date: f.date })))
	}

	tlUser = createTLCurrentUser({
		userPreferences: computed('user prefs', () => {
			const user = this.getUser()
			return {
				...(Object.fromEntries(
					UserPreferencesKeys.map((key) => [key, user[key]])
				) as unknown as TLUserPreferences),
				id: this.userId,
			}
		}),
		setUserPreferences: ({ id: _, ...others }: Partial<TLUserPreferences>) => {
			const user = this.getUser()

			const nonNull = Object.fromEntries(
				Object.entries(others).filter(([key, value]) => value !== null || key === 'inputMode')
			) as Partial<TLUserPreferences>

			this.z.mutate.user.update({
				id: user.id,
				...(nonNull as any),
			})
		},
	})

	getUserOwnFiles() {
		return compact(this.getUserFileStates().map((f) => f.file))
	}

	getUserFileStates() {
		return this.fileStates$.get()
	}

	// Store stable workspace file ordering for each workspace to prevent jumping when files are edited
	lastWorkspaceFileOrderings = new Map<
		string,
		Array<{
			fileId: TlaFile['id']
			date: number
		}>
	>()

	@computed({ isEqual })
	getMyFiles() {
		return this.getWorkspaceFilesSorted(this.getHomeWorkspaceId())
	}

	/**
	 * The id of the user's most recently visited file, or null if they have none. Skips files the
	 * user can no longer access — their `file` relation comes back null (moved/revoked) or flagged
	 * deleted — so the next available file wins; falls back to the top of the in-scope list when
	 * none have been visited. Recency comes from per-user `file_state`, so it follows the user
	 * across devices.
	 *
	 * @param workspaceId - When provided, only files visible in that workspace are considered.
	 */
	getMostRecentFileId(workspaceId?: string): string | null {
		// In-scope files, also used as the fallback when none have been visited.
		const scopedFiles = workspaceId ? this.getWorkspaceFilesSorted(workspaceId) : this.getMyFiles()
		const fileIdsInScope = workspaceId ? new Set(scopedFiles.map((f) => f.fileId)) : null

		let mostRecent: { fileId: string; date: number } | null = null
		for (const state of this.getUserFileStates()) {
			if (fileIdsInScope && !fileIdsInScope.has(state.fileId)) continue
			if (!state.file || state.file.isDeleted) continue
			// Rank by actual visits only. A created-but-never-opened file has null visit timestamps
			// (its `createdAt` must not let it outrank a genuinely visited file); it defers to the
			// `scopedFiles` fallback below.
			const date = getFileVisitDate(state)
			if (date === undefined) continue
			if (!mostRecent || date > mostRecent.date) mostRecent = { fileId: state.fileId, date }
		}

		return mostRecent?.fileId ?? scopedFiles[0]?.fileId ?? null
	}

	private canCreateNewFile(workspaceId: string, count = 1) {
		// Count only files the workspace actually owns — not guest files (shared files the
		// user opened) or mislinked rows that getWorkspaceFilesSorted hides. Counting those
		// would let the limit fire on files the user can neither see nor remove. createFile
		// runs no server-side file-limit check, so this is the only gate; scoping it to owned
		// files keeps it to what the user can actually manage.
		const membership = this.getWorkspaceMembership(workspaceId)
		if (!membership) return true
		const nonDeletedCount = membership.groupFiles.filter(
			(gf) => gf.file && !gf.file.isDeleted && gf.file.owningGroupId === workspaceId
		).length
		return nonDeletedCount + count <= this.config.maxNumberOfFiles
	}

	private showMaxFilesToast() {
		trackEvent('max-files-reached')
		this.toasts?.addToast({
			title: this.getIntl().formatMessage(this.messages.max_files_title),
			description: this.getIntl().formatMessage(this.messages.max_files_reached),
			keepOpen: true,
		})
	}

	isPinned(fileId: string, workspaceId: string) {
		return this.getWorkspaceFilesSorted(workspaceId).some((f) => f.fileId === fileId && f.isPinned)
	}

	// A workspace's welcome file is seeded once, when the workspace is created. Its
	// `createWorkspace` mutation lands before the file does, so the workspace briefly appears
	// empty; this single-flights the seed per workspace so a double-submit (or any concurrent
	// caller) shares one creation and all receive its result, rather than creating duplicates.
	// Cleared once settled.
	private readonly workspaceWelcomeFileSeeds = new Map<
		string,
		Promise<Result<{ fileId: string }, 'max number of files reached' | 'mutation rejected'>>
	>()

	/**
	 * Seed a newly-created workspace's welcome file: a named file whose content the sync worker
	 * resolves from the welcome template (or a committed default). Called once at workspace
	 * creation — not on later empty opens, which get a blank file (see useSwitchToWorkspace) —
	 * and single-flighted so a double-submit shares the same creation.
	 */
	createWorkspaceWelcomeFile(
		workspaceId: string
	): Promise<Result<{ fileId: string }, 'max number of files reached' | 'mutation rejected'>> {
		const inFlight = this.workspaceWelcomeFileSeeds.get(workspaceId)
		if (inFlight) return inFlight

		const seed = this.createFile({
			workspaceId,
			name: this.getIntl().formatMessage(this.messages.new_workspace_file_name),
			createSource: WELCOME_CREATE_SOURCE,
		})
		this.workspaceWelcomeFileSeeds.set(workspaceId, seed)
		seed.finally(() => {
			if (this.workspaceWelcomeFileSeeds.get(workspaceId) === seed) {
				this.workspaceWelcomeFileSeeds.delete(workspaceId)
			}
		})
		return seed
	}

	/**
	 * The in-flight welcome-file seed for a workspace, if one is still creating. The empty-workspace
	 * open path uses this to await a just-created workspace's welcome file rather than racing it with
	 * a duplicate blank file. Returns undefined once the seed settles (or if none was started).
	 */
	getPendingWorkspaceWelcomeFile(workspaceId: string) {
		return this.workspaceWelcomeFileSeeds.get(workspaceId)
	}

	async createFile({
		fileId = uniqueId(),
		workspaceId = this.getHomeWorkspaceId(),
		name = this.getFallbackFileName(Date.now()),
		createSource = null,
	}: {
		fileId?: string
		workspaceId?: string
		name?: string
		createSource?: string | null
	} = {}): Promise<
		Result<{ fileId: string }, 'max number of files reached' | 'mutation rejected'>
	> {
		if (!this.canCreateNewFile(workspaceId)) {
			this.showMaxFilesToast()
			return Result.err('max number of files reached')
		}

		this.storeNewRoomCreationTracking(fileId, createSource, Date.now())
		// Zero mutator promises never reject — failures resolve with {type: 'error'} — so a
		// try/catch here would be dead code and a failed create would navigate to a file that
		// rolls back.
		const res = await this.z.mutate.createFile({
			fileId,
			workspaceId,
			name,
			createSource,
			time: Date.now(),
		}).client
		if (res.type === 'error') {
			this.showMutationRejectionToast(res.error)
			return Result.err('mutation rejected')
		}

		return Result.ok({ fileId })
	}

	/**
	 * Get and remove the creation start time and source for a file (used for tracking new room creation duration)
	 */
	getAndClearNewRoomCreationStartTime(
		fileId: string
	): { startTime: number; source: string } | null {
		const creationData = this.newRoomCreationStartTimes.get(fileId) ?? null
		if (creationData !== null) {
			this.newRoomCreationStartTimes.delete(fileId)
		}
		return creationData
	}

	/**
	 * Store new room creation timing data with analytics-friendly source mapping
	 */
	private storeNewRoomCreationTracking(
		fileId: string,
		createSource: string | null,
		startTime: number
	): void {
		let analyticsSource: string

		if (!createSource) {
			analyticsSource = 'create-blank-file' // Default for button clicks
		} else if (createSource === WELCOME_CREATE_SOURCE) {
			analyticsSource = 'welcome'
		} else if (createSource.startsWith(`${LOCAL_FILE_PREFIX}/`)) {
			analyticsSource = 'slurp'
		} else if (createSource.startsWith(`${FILE_PREFIX}/`)) {
			analyticsSource = 'duplicate'
		} else if (createSource.startsWith(`${ROOM_PREFIX}/`)) {
			analyticsSource = 'legacy-import'
		} else {
			analyticsSource = 'other'
		}

		// Store the creation start time and source for tracking
		this.newRoomCreationStartTimes.set(fileId, {
			startTime,
			source: analyticsSource,
		})
	}

	getFallbackFileName(time: number) {
		const createdAt = new Date(time)
		const format = getDateFormat(createdAt)
		return this.getIntl().formatDate(createdAt, format)
	}

	getFileName(file: TlaFile | string | null, useDateFallback: false): string | undefined
	getFileName(file: TlaFile | string | null, useDateFallback?: true): string
	getFileName(file: TlaFile | string | null, useDateFallback = true) {
		if (typeof file === 'string') {
			file = this.getFile(file)
		}
		if (!file) {
			// possibly a published file
			return ''
		}

		if (typeof file.name === 'undefined') {
			captureException(new Error('file name is undefined somehow: ' + JSON.stringify(file)))
		}
		// need a ? here because we were seeing issues on sentry where file.name was undefined
		const name = file.name?.trim()
		if (name) {
			return name
		}

		if (useDateFallback) {
			return this.getFallbackFileName(file.createdAt)
		}

		return
	}

	slurpFile() {
		return this.createFile({
			createSource: `${LOCAL_FILE_PREFIX}/${getScratchPersistenceKey()}`,
		})
	}

	toggleFileShared(fileId: string) {
		const file = this.getFile(fileId)
		if (!file) throw Error('no file with id ' + fileId)

		this.z.mutate.file.update({
			id: fileId,
			shared: !file.shared,
		})
	}

	publishFile(fileId: string) {
		const file = this.getFile(fileId)
		if (!file) throw Error(`No file with that id`)

		// We're going to bake the name of the file, if it's undefined
		const name = this.getFileName(file)

		// Optimistic update
		this.z.mutate.file.update({
			id: fileId,
			name,
			published: true,
			lastPublished: Date.now(),
		})
	}

	getFile(fileId?: string): TlaFile | null {
		if (!fileId) return null
		for (const membership of this.getWorkspaceMemberships()) {
			const groupFile = membership.groupFiles.find((gf) => gf.fileId === fileId)
			if (groupFile) return groupFile.file ?? null
		}
		return null
	}

	canUpdateFile(fileId: string): boolean {
		const file = this.getFile(fileId)
		if (!file) return false
		if (file.owningGroupId) {
			const role = this.getWorkspaceMembership(file.owningGroupId)?.role
			return can(role, 'accessFiles')
		}
		return false
	}

	requireFile(fileId: string): TlaFile {
		return assertExists(this.getFile(fileId), 'no file with id ' + fileId)
	}

	unpublishFile(fileId: string) {
		const file = this.requireFile(fileId)
		if (!this.canUpdateFile(fileId)) throw Error('user cannot edit that file')

		if (!file.published) return

		// Optimistic update
		this.z.mutate.file.update({
			id: fileId,
			published: false,
		})
	}

	/**
	 * Remove the user's file state and the workspace's link to a file, deleting the file itself only
	 * if that workspace is the one that owns it.
	 */
	async deleteOrForgetFile(
		fileId: string,
		workspaceId: string = this.getHomeWorkspaceId()
	): Promise<boolean> {
		// Optimistic update, remove file and file states. Zero mutator promises never reject —
		// failures resolve with {type: 'error'} — so the result has to be checked, not caught.
		const res = await this.z.mutate.removeFileFromWorkspace({ fileId, workspaceId }).client
		if (res.type === 'error') {
			this.showMutationRejectionToast(res.error)
			return false
		}
		// Otherwise the next `/` load would walk straight back into the room and re-add the file.
		if (getLastVisitedFileId(this.userId) === fileId) clearLastVisitedFile()
		return true
	}

	setFileSharedLinkType(fileId: string, sharedLinkType: TlaFile['sharedLinkType'] | 'no-access') {
		if (!this.canUpdateFile(fileId)) throw Error('user cannot edit that file')

		if (sharedLinkType === 'no-access') {
			this.z.mutate.file.update({ id: fileId, shared: false })
			return
		}
		this.z.mutate.file.update({ id: fileId, shared: true, sharedLinkType })
	}

	updateUser(partial: Partial<TlaUser>) {
		const user = this.getUser()
		return this.z.mutate.user.update({
			id: user.id,
			...partial,
		})
	}

	updateUserExportPreferences(
		exportPreferences: Partial<
			Pick<TlaUser, 'exportFormat' | 'exportPadding' | 'exportBackground' | 'exportTheme'>
		>
	) {
		this.updateUser(exportPreferences)
	}

	getFileState(fileId: string) {
		return this.getUserFileStates().find((f) => f.fileId === fileId)
	}

	/** Same test getMostRecentFileId applies: a visit whose file is gone (moved, revoked, deleted) doesn't count. */
	isFileVisitable(fileId: string) {
		const file = this.getFileState(fileId)?.file
		return !!file && !file.isDeleted
	}

	updateFileState(fileId: string, partial: Omit<TlaFileStatePartial, 'fileId' | 'userId'>) {
		const file = this.getFile(fileId)
		if (!file || file.isDeleted) return
		this.z.mutate.file_state.update({ ...partial, fileId, userId: this.userId })
	}

	markCommentRead(commentId: string) {
		this.z.mutate.comment.markRead({ commentId, readAt: Date.now() })
	}

	markCommentsRead(commentIds: string[]) {
		if (commentIds.length === 0) return
		this.z.mutate.comment.markManyRead({ commentIds, readAt: Date.now() })
	}

	updateFile(fileId: string, partial: Partial<TlaFile>) {
		this.z.mutate.file.update({ id: fileId, ...partial })
	}

	async onFileEnter(fileId: string) {
		this.z.mutate.onEnterFile({ fileId, time: Date.now() })
	}

	static async create(opts: {
		userId: string
		email?: string | null
		flags: FeatureFlags
		/** Clerk session token, for this app's own REST endpoints. */
		getToken(): Promise<string | undefined>
		/** Token for the Zero connection — see {@link getZeroAuth} on the worker for why it differs. */
		getZeroToken(): Promise<string | undefined>
		onClientTooOld(): void
		trackEvent: TLAppUiContextType
		navigate: ReturnType<typeof useNavigate>
		/** Settles a bootstrap the caller no longer wants, e.g. a tab that never became visible. */
		signal?: AbortSignal
	}) {
		// This is an issue: we may have a user record but not in the store.
		// Could be just old accounts since before the server had a version
		// of the store... but we should probably identify that better.

		const { id: _id, name: _name, color, ...restOfPreferences } = getUserPreferences()
		// Get initial token before creating Zero instance
		const initialZeroToken = await opts.getZeroToken()
		const app = new TldrawApp(
			opts.userId,
			initialZeroToken,
			opts.getToken,
			opts.getZeroToken,
			opts.onClientTooOld,
			opts.trackEvent,
			opts.navigate,
			opts.flags,
			opts.email
		)
		// @ts-expect-error
		window.app = app
		try {
			await app.preload(opts.signal)
			app.startNotificationFeeds()
		} catch (e) {
			// Don't leave the half-built app's Zero connection and timers running behind the
			// error page the caller shows for this.
			app.dispose()
			throw e
		}
		const user = app.getUser()
		if (user.color === '___INIT___') {
			app.updateUser({
				color: color ?? defaultUserPreferences.color,
				...restOfPreferences,
				inputMode: restOfPreferences.inputMode ?? null,
				locale: restOfPreferences.locale ?? null,
				animationSpeed: restOfPreferences.animationSpeed ?? null,
				areKeyboardShortcutsEnabled: restOfPreferences.areKeyboardShortcutsEnabled ?? null,
				edgeScrollSpeed: restOfPreferences.edgeScrollSpeed ?? null,
				colorScheme: restOfPreferences.colorScheme ?? null,
				isSnapMode: restOfPreferences.isSnapMode ?? null,
				isWrapMode: restOfPreferences.isWrapMode ?? null,
				isDynamicSizeMode: restOfPreferences.isDynamicSizeMode ?? null,
				isPasteAtCursorMode: restOfPreferences.isPasteAtCursorMode ?? null,
				enhancedA11yMode: restOfPreferences.enhancedA11yMode ?? null,
				isZoomDirectionInverted: restOfPreferences.isZoomDirectionInverted ?? null,
			})

			opts.trackEvent('create-user', { source: 'app' })
			updateLocalSessionState((state) => ({ ...state, shouldShowWelcomeDialog: true }))
		}
		return { app, userId: user.id }
	}

	getIntl() {
		const intl = createIntl()
		if (intl) return intl
		// intl should exists since IntlWrapper should create it before we get here, but let's use this just in case
		setupCreateIntl({
			defaultLocale: 'en',
			locale: this.user$.get()?.locale ?? 'en',
			messages: {},
		})
		return createIntl()!
	}

	async uploadTldrFiles(
		files: File[],
		opts: {
			/** Where the import started; reported as the `create-file` source for each file. */
			source: TLAppUiEventSource
			onFirstFileUploaded?(fileId: string): void
			workspaceId?: string
			onUploadError?(): void
		}
	): Promise<void> {
		let { onFirstFileUploaded } = opts
		const { source, workspaceId, onUploadError } = opts
		const totalFiles = files.length
		let uploadedFiles = 0
		if (totalFiles === 0) return
		// createFile runs this check too, but only after the snapshot and its assets are already
		// uploaded — which would orphan a room in R2 and stack a generic error on the limit toast.
		// Check the whole batch: with one slot left, a two-file import would otherwise create the
		// first file and then upload the second before createFile rejects it.
		if (!this.canCreateNewFile(workspaceId ?? this.getHomeWorkspaceId(), totalFiles)) {
			this.showMaxFilesToast()
			onUploadError?.()
			return
		}

		// this is only approx since we upload the files in pieces and they are base64 encoded
		// in the json blob, so this will usually be a big overestimate. But that's fine because
		// if the upload finishes before the number hits 100% people are pleasantly surprised.
		const approxTotalBytes = files.reduce((acc, f) => acc + f.size, 0)
		let bytesUploaded = 0
		const getApproxPercentage = () =>
			Math.min(Math.round((bytesUploaded / approxTotalBytes) * 100), 100)
		const updateProgress = () => updateToast({ description: `${getApproxPercentage()}%` })

		// only bother showing the percentage if it's going to take a while

		let uploadingToastId = undefined as undefined | string
		let didFinishUploading = false

		// give it a second before we show the toast, in case the upload is fast
		const uploadingToastTimeout = setTimeout(() => {
			if (didFinishUploading || this.abortController.signal.aborted) return
			// if it's close to the end, don't show the progress toast
			if (getApproxPercentage() > 50) return
			uploadingToastId = this.toasts?.addToast({
				severity: 'info',
				title: this.getIntl().formatMessage(this.messages.uploadingTldrFiles, {
					total: totalFiles,
					uploaded: uploadedFiles + 1,
				}),

				description: `${getApproxPercentage()}%`,
				keepOpen: true,
			})
		}, 800)

		const updateToast = (args: { title?: string; description?: string }) => {
			if (!uploadingToastId) return
			this.toasts?.toasts.update((toasts) =>
				toasts.map((t) =>
					t.id === uploadingToastId
						? {
								...t,
								...args,
							}
						: t
				)
			)
		}

		for (const f of files) {
			const res = await this.uploadTldrFile(
				f,
				(bytes) => {
					bytesUploaded += bytes
					updateProgress()
				},
				workspaceId
			).catch((e) => Result.err(e))
			if (!res.ok) {
				clearTimeout(uploadingToastTimeout)
				if (uploadingToastId) this.toasts?.removeToast(uploadingToastId)
				this.toasts?.addToast({
					severity: 'error',
					title: this.getIntl().formatMessage(this.messages.unknown_error),
					keepOpen: true,
				})
				console.error(res.error)
				onUploadError?.()
				return
			}

			updateToast({
				title: this.getIntl().formatMessage(this.messages.uploadingTldrFiles, {
					total: totalFiles,
					uploaded: ++uploadedFiles + 1,
				}),
			})

			this.trackEvent('create-file', { source })

			if (onFirstFileUploaded) {
				onFirstFileUploaded(res.value.fileId)
				onFirstFileUploaded = undefined
			}
		}
		didFinishUploading = true

		if (uploadingToastId) this.toasts?.removeToast(uploadingToastId)

		if (totalFiles > 1) {
			this.toasts?.addToast({
				severity: 'success',
				title: this.getIntl().formatMessage(this.messages.addingTldrFiles, {
					total: files.length,
				}),
				keepOpen: true,
			})
		}
	}

	private async uploadTldrFile(
		file: File,
		onProgress?: (bytesUploadedSinceLastProgressUpdate: number) => void,
		workspaceId?: string
	) {
		const json = await file.text()
		const parseFileResult = parseTldrawJsonFile({
			schema: createTLSchema(),
			json,
		})

		if (!parseFileResult.ok) {
			return Result.err('could not parse file')
		}

		const snapshot = parseFileResult.value.getStoreSnapshot()

		for (const record of Object.values(snapshot.store)) {
			if (
				record.typeName !== 'asset' ||
				record.type === 'bookmark' ||
				!record.props.src?.startsWith('data:')
			) {
				snapshot.store[record.id] = record
				continue
			}
			const src = record.props.src
			const file = await dataUrlToFile(
				src,
				record.props.name,
				record.props.mimeType ?? 'application/octet-stream'
			)
			// TODO: this creates duplicate versions of the assets because we'll re-upload them when the user opens
			// the file to associate them with the file id. To avoid this we'd need a way to create the file row
			// in postgres so we can do the association while uploading the first time. Or just tolerate foreign key
			// constraints being violated for a moment.
			const assetsStore = multiplayerAssetStore()
			const { src: newSrc } = await assetsStore.upload(record, file, this.abortController.signal)
			onProgress?.(file.size)
			snapshot.store[record.id] = {
				...record,
				props: {
					...record.props,
					src: newSrc,
				},
			}
		}
		const body = JSON.stringify({
			snapshots: [
				{
					schema: snapshot.schema,
					snapshot: snapshot.store,
				} satisfies Snapshot,
			],
		})

		const res = await fetch(TLDR_FILE_ENDPOINT, { method: 'POST', body })
		onProgress?.(body.length)
		if (!res.ok) {
			throw Error('could not upload file ' + (await res.text()))
		}
		const response = (await res.json()) as CreateFilesResponseBody
		if (response.error) {
			throw Error(response.message)
		}
		const fileId = response.slugs[0]
		// `||` not `??`: File.name is never nullish, so the document-name fallback was unreachable
		// and a file called `.tldr` would reach createFile with an empty name (bad_request).
		const name =
			file.name.replace(/\.tldr$/, '').trim() ||
			Object.values(snapshot.store)
				.find((d): d is TLDocument => d.typeName === 'document')
				?.name?.trim() ||
			undefined

		return this.createFile({ fileId, name, workspaceId })
	}

	sidebarState = atom('sidebar state', {
		renameState: null as null | {
			fileId: string
			workspaceId: string
		},
		// The current sidebar file-search query. Empty string means no filter.
		searchQuery: '',
	})

	/** Returns false when there is no invite link to copy yet (e.g. right after creation). */
	copyWorkspaceInvite(workspaceId: string, showToast = true): boolean {
		// The home workspace can't be invited to.
		if (workspaceId === this.getHomeWorkspaceId()) return false
		const group = this.getWorkspaceMembership(workspaceId)?.group
		if (!group?.inviteSecret) return false

		copyTextToClipboard(routes.tlaInvite(group.inviteSecret, { asUrl: true }))

		if (showToast) {
			this.toasts?.addToast({
				id: 'copied-invite-link',
				title: 'Copied invite link',
			})
		}

		this.trackEvent('copy-share-link', { source: 'sidebar' })
		return true
	}

	async acceptWorkspaceInvite(inviteSecret: string) {
		const response = await fetch(`/api/app/invite/${inviteSecret}/accept`, {
			method: 'POST',
		})

		// A gateway error page or the router's own 401 body isn't an AcceptInviteResponseBody;
		// parsing it first would throw past the toast below.
		const payload = (await response.json().catch(() => null)) as AcceptInviteResponseBody | null

		if (!payload || payload.error || !response.ok) {
			this.toasts?.addToast({
				severity: 'error',
				title: 'Error accepting invite',
				description: payload?.message ?? 'Please try again.',
			})
			this.navigate(routes.tlaRoot())
			return
		}

		this.trackEvent('accept-workspace-invite', { source: 'sidebar' })

		// wait for the workspace to appear in the store, but not forever
		let attempts = 0
		while (!this.getWorkspaceMembership(payload.workspaceId)) {
			if (attempts++ > 100) {
				this.toasts?.addToast({
					severity: 'error',
					title: 'Error accepting invite',
					description: 'Please try again.',
				})
				this.navigate(routes.tlaRoot())
				return
			}
			await sleep(100)
		}

		// Clear any existing ordering for this new workspace to get fresh ordering
		this.lastWorkspaceFileOrderings.delete(payload.workspaceId)

		if (!this.navigateToWorkspaceFiles(payload.workspaceId)) {
			this.navigate(routes.tlaRoot())
		}
	}

	navigateToWorkspaceFiles(workspaceId: string, opts: { replace?: boolean } = {}) {
		const files = this.getWorkspaceFilesSorted(workspaceId)

		if (!files.length) {
			return false
		}

		this.navigate(routes.tlaFile(files[0]!.fileId), opts)
		return true
	}
}
